import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    IconButton, Paper, Switch, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Tooltip,
    Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { API_PATH, appContext } from '../../../App';
import TableLoader from '../GlobalComponents/TableLoader';

// Proxy (segproxy) reward settlements from segments-pool, via cloud-mining's /segPoolRewardDistribution pass-through

type SettlementStatus = 'PENDING' | 'HELD' | 'APPROVED' | 'FINALIZED';

interface SettlementRun {
    id: number;
    settlementDate: string | number;
    coin: string;
    status: SettlementStatus;
    f2poolAmount: number | null;
    f2poolHashrateTh: number | null;
    segpoolHashrateTh: number | null;
    deviationPercent: number | null;
    platformFeeAmount: number | null;
    distributedAmount: number | null;
    unassignedAmount: number | null;
    workerRowCount: number | null;
    accountRowCount: number | null;
    approvedBy: string | null;
    approvedAt: string | number | null;
    errorMessage: string | null;
    finalizedAt: string | number | null;
}

interface UnassignedShare {
    id: number;
    settlementDate: string | number;
    coin: string;
    workerId: string;
    userLabel: string;
    hashRateTh: number;
    amount: number;
}

// the held share as segpool returns it once assigned
interface AssignedShare extends UnassignedShare {
    assignedAccount: string | null;
}

interface Overview {
    runs: SettlementRun[];
    unassigned: UnassignedShare[];
    f2poolAccounts: Record<string, string>;
}

interface WorkerShare {
    workerId: string;
    user: string | null;
    hashRateTh: number;
    sharePercent: number;
    hasAccount: boolean;
}

// a function, not a constant: App imports this module (via MainView), so API_PATH is not initialized
// yet while this module is being evaluated
const base = (): string => `${API_PATH}/segPoolRewardDistribution/proxy-settlements`;

const STATUS_COLOR: Record<SettlementStatus, 'error' | 'default' | 'warning' | 'success'> = {
    HELD: 'error',
    PENDING: 'default',
    APPROVED: 'warning',
    FINALIZED: 'success',
};

// java.sql.Date arrives as "yyyy-MM-dd", or as epoch millis if the server writes dates as timestamps
const dateOf = (value: string | number): string =>
    typeof value === 'number' ? new Date(value).toISOString().slice(0, 10) : value.slice(0, 10);

const dateTimeOf = (value: string | number | null): string =>
    value == null ? '-' : new Date(value).toLocaleString();

const th = (value: number | null | undefined): string => (value == null ? '-' : value.toFixed(2));
const coinAmount = (value: number | null | undefined): string => (value == null ? '-' : value.toFixed(8));
const percent = (value: number | null | undefined): string => (value == null ? '-' : `${value.toFixed(2)}%`);

const errorMessageOf = (error: any, fallback: string): string => {
    const data = error?.response?.data;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data).message || fallback;
        } catch {
            return data || fallback;
        }
    }
    return data?.message || fallback;
};

export default function ProxySettlementList() {
    const { authRole } = React.useContext(appContext);
    const canAct = authRole === 'ADMIN';

    const [overview, setOverview] = useState<Overview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'runs' | 'unassigned'>('runs');
    const [heldOnly, setHeldOnly] = useState(false);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [approving, setApproving] = useState<SettlementRun | null>(null);
    const [assigning, setAssigning] = useState<UnassignedShare | null>(null);
    const [assignAccount, setAssignAccount] = useState('');
    const [assignBusy, setAssignBusy] = useState(false);
    const [assignError, setAssignError] = useState<string | null>(null);

    const fetchOverview = useCallback(() => {
        setLoading(true);
        axios.get<Overview>(base(), getAxiosConfig())
            .then(response => {
                setOverview(response.data);
                setError(null);
            })
            .catch(err => setError(errorMessageOf(err, 'Failed to load proxy settlements.')))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchOverview();
    }, [fetchOverview]);

    const runs = useMemo(() => {
        const all = overview?.runs ?? [];
        const shown = heldOnly ? all.filter(run => run.status === 'HELD') : all;
        // HELD first, then newest first
        return shown.slice().sort((a, b) =>
            Number(b.status === 'HELD') - Number(a.status === 'HELD')
            || dateOf(b.settlementDate).localeCompare(dateOf(a.settlementDate)));
    }, [overview, heldOnly]);

    const act = (run: SettlementRun, action: 'recheck' | 'approve') => {
        const date = dateOf(run.settlementDate);
        const key = `${run.coin}/${date}`;
        setBusyKey(key);
        setNotice(null);
        setError(null);
        axios.post<SettlementRun>(`${base()}/${run.coin}/${date}/${action}`, null, getAxiosConfig())
            .then(response => {
                setNotice(`${run.coin} ${date}: ${action === 'approve' ? 'approved' : 'rechecked'}, now ${response.data.status}.`);
                fetchOverview();
            })
            .catch(err => setError(errorMessageOf(err, `Could not ${action} ${run.coin} ${date}.`)))
            .finally(() => {
                setBusyKey(null);
                setApproving(null);
            });
    };

    const openAssign = (share: UnassignedShare) => {
        setAssignAccount(share.userLabel ?? '');
        setAssignError(null);
        setAssigning(share);
    };

    const assign = (share: UnassignedShare) => {
        const account = assignAccount.trim();
        setAssignBusy(true);
        setAssignError(null);
        setNotice(null);
        axios.post<AssignedShare>(`${base()}/unassigned/${share.id}/assign`, null,
            { ...getAxiosConfig(), params: account ? { account } : undefined })
            .then(response => {
                setNotice(`${coinAmount(share.amount)} ${share.coin} of ${share.workerId} for `
                    + `${dateOf(share.settlementDate)} assigned to ${response.data.assignedAccount ?? account}.`);
                setAssigning(null);
                fetchOverview();
            })
            // a refusal stays in the dialog, so the account can be corrected and tried again
            .catch(err => setAssignError(errorMessageOf(err, 'Could not assign this share.')))
            .finally(() => setAssignBusy(false));
    };

    const accounts = overview?.f2poolAccounts ?? {};

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Proxy Rewards</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Daily splits of the segproxy F2Pool account's income between proxy workers. A date is HELD when
                F2Pool's hashrate and segpool's recorded hashrate differ by more than the tolerance; held dates
                are rechecked every hour for 7 days.
            </Typography>

            {Object.keys(accounts).length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {Object.entries(accounts).map(([coin, account]) => (
                        <Chip key={coin} variant="outlined" label={`${coin}: comparing against F2Pool account "${account || 'none'}"`} />
                    ))}
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
                    <Tab label="Settlement runs" value="runs" />
                    <Tab label={`Held shares (${overview?.unassigned.length ?? 0})`} value="unassigned" />
                </Tabs>
                {activeTab === 'runs' && (
                    <FormControlLabel
                        control={<Switch checked={heldOnly} onChange={event => setHeldOnly(event.target.checked)} />}
                        label="Held only"
                    />
                )}
            </Box>

            {loading && !overview ? (
                <TableLoader />
            ) : activeTab === 'runs' ? (
                <Paper sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell />
                                <TableCell>Date</TableCell>
                                <TableCell>Coin</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">F2Pool TH/s</TableCell>
                                <TableCell align="right">Segpool TH/s</TableCell>
                                <TableCell align="right">Deviation</TableCell>
                                <TableCell align="right">F2Pool amount</TableCell>
                                <TableCell align="right">Fee</TableCell>
                                <TableCell align="right">Distributed</TableCell>
                                <TableCell align="right">Held</TableCell>
                                <TableCell>Approved</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {runs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={13} align="center">No proxy settlement runs.</TableCell>
                                </TableRow>
                            ) : runs.map(run => (
                                <RunRow
                                    key={run.id}
                                    run={run}
                                    canAct={canAct}
                                    busy={busyKey === `${run.coin}/${dateOf(run.settlementDate)}`}
                                    onRecheck={() => act(run, 'recheck')}
                                    onApprove={() => setApproving(run)}
                                />
                            ))}
                        </TableBody>
                    </Table>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                        Only the 60 most recent settlement runs are shown: the last 60 days while BTC is the only
                        coin settled.
                    </Typography>
                </Paper>
            ) : (
                <Paper sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Coin</TableCell>
                                <TableCell>Worker</TableCell>
                                <TableCell>User label</TableCell>
                                <TableCell align="right">TH/s</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                {canAct && <TableCell>Actions</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(overview?.unassigned ?? []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={canAct ? 7 : 6} align="center">No held shares.</TableCell>
                                </TableRow>
                            ) : overview!.unassigned.map(share => (
                                <TableRow key={share.id}>
                                    <TableCell>{dateOf(share.settlementDate)}</TableCell>
                                    <TableCell>{share.coin}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{share.workerId}</TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{share.userLabel || '-'}</TableCell>
                                    <TableCell align="right">{th(share.hashRateTh)}</TableCell>
                                    <TableCell align="right">{coinAmount(share.amount)}</TableCell>
                                    {canAct && (
                                        <TableCell>
                                            <Button size="small" variant="outlined" onClick={() => openAssign(share)}>
                                                Assign
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>
                        Shares of proxy workers whose user matched no active segpool account when their date was
                        split. They are not paid to anyone until an admin assigns them to an account.
                    </Typography>
                </Paper>
            )}

            <Dialog open={approving != null} onClose={() => busyKey == null && setApproving(null)} maxWidth="sm" fullWidth>
                {approving && (
                    <>
                        <DialogTitle>Approve {approving.coin} {dateOf(approving.settlementDate)}?</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ mb: 2 }}>
                                F2Pool reported <b>{th(approving.f2poolHashrateTh)} TH/s</b> and segpool recorded{' '}
                                <b>{th(approving.segpoolHashrateTh)} TH/s</b> ({percent(approving.deviationPercent)} apart),
                                comparing against F2Pool account <b>{accounts[approving.coin] || 'unknown'}</b>.
                            </Typography>
                            <Alert severity="warning">
                                Approving splits the whole {coinAmount(approving.f2poolAmount)} {approving.coin} (less the
                                platform fee) between the workers segpool recorded for this date, in proportion to their
                                recorded hashrate. Hashrate F2Pool saw but segpool did not record is paid to them too.
                                This cannot be undone here.
                            </Alert>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setApproving(null)} disabled={busyKey != null}>Cancel</Button>
                            <Button color="warning" variant="contained" disabled={busyKey != null}
                                    onClick={() => act(approving, 'approve')}>
                                Approve
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog open={assigning != null} onClose={() => !assignBusy && setAssigning(null)} maxWidth="sm" fullWidth>
                {assigning && (
                    <>
                        <DialogTitle>Assign held share</DialogTitle>
                        <DialogContent>
                            <Typography sx={{ mb: 2 }}>
                                <b>{coinAmount(assigning.amount)} {assigning.coin}</b> for{' '}
                                <b>{dateOf(assigning.settlementDate)}</b> ({th(assigning.hashRateTh)} TH/s), earned by
                                worker <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {assigning.workerId}
                                </Box>.
                            </Typography>
                            <TextField
                                label="Segpool account"
                                value={assignAccount}
                                onChange={event => setAssignAccount(event.target.value)}
                                fullWidth
                                size="small"
                                disabled={assignBusy}
                                helperText="The account to pay. It is the worker's own user unless you change it, and it must already exist in segpool."
                                sx={{ mb: 2 }}
                                inputProps={{ style: { fontFamily: 'monospace' } }}
                            />
                            {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}
                            <Alert severity="warning">
                                Assigning adds this amount to the account's proxy reward for that date, and it is paid
                                with the account's next payout. This cannot be undone here.
                            </Alert>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setAssigning(null)} disabled={assignBusy}>Cancel</Button>
                            <Button color="warning" variant="contained" disabled={assignBusy || !assignAccount.trim()}
                                    onClick={() => assign(assigning)}>
                                {assignBusy ? 'Assigning…' : 'Assign'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
}

interface RunRowProps {
    run: SettlementRun;
    canAct: boolean;
    busy: boolean;
    onRecheck: () => void;
    onApprove: () => void;
}

function RunRow({ run, canAct, busy, onRecheck, onApprove }: RunRowProps) {
    const [open, setOpen] = useState(false);
    const [workers, setWorkers] = useState<WorkerShare[] | null>(null);
    const [workersError, setWorkersError] = useState<string | null>(null);
    const date = dateOf(run.settlementDate);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && workers == null) {
            axios.get<WorkerShare[]>(`${base()}/${run.coin}/${date}/workers`, getAxiosConfig())
                .then(response => setWorkers(response.data))
                .catch(err => setWorkersError(errorMessageOf(err, 'Failed to load workers.')));
        }
    };

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
                <TableCell>
                    <IconButton size="small" onClick={toggle}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell>{date}</TableCell>
                <TableCell>{run.coin}</TableCell>
                <TableCell>
                    <Tooltip title={run.errorMessage ?? ''}>
                        <Chip size="small" color={STATUS_COLOR[run.status] ?? 'default'} label={run.status} />
                    </Tooltip>
                </TableCell>
                <TableCell align="right">{th(run.f2poolHashrateTh)}</TableCell>
                <TableCell align="right">{th(run.segpoolHashrateTh)}</TableCell>
                <TableCell align="right">{percent(run.deviationPercent)}</TableCell>
                <TableCell align="right">{coinAmount(run.f2poolAmount)}</TableCell>
                <TableCell align="right">{coinAmount(run.platformFeeAmount)}</TableCell>
                <TableCell align="right">{coinAmount(run.distributedAmount)}</TableCell>
                <TableCell align="right">{coinAmount(run.unassignedAmount)}</TableCell>
                <TableCell>
                    {run.approvedBy ? `${run.approvedBy}, ${dateTimeOf(run.approvedAt)}` : '-'}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {canAct && run.status !== 'FINALIZED' && (
                        <Button size="small" onClick={onRecheck} disabled={busy} sx={{ mr: 1 }}>
                            {busy ? 'Working…' : 'Recheck now'}
                        </Button>
                    )}
                    {canAct && run.status === 'HELD' && (
                        <Button size="small" color="warning" variant="outlined" onClick={onApprove} disabled={busy}>
                            Approve
                        </Button>
                    )}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={13} sx={{ py: 0 }}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ my: 2 }}>
                            {run.errorMessage && <Alert severity="info" sx={{ mb: 2 }}>{run.errorMessage}</Alert>}
                            {run.finalizedAt != null && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Finalized {dateTimeOf(run.finalizedAt)}: {run.workerRowCount ?? 0} worker rows,{' '}
                                    {run.accountRowCount ?? 0} account rows.
                                </Typography>
                            )}
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Proxy workers segpool recorded for {date} (current Redis data)
                            </Typography>
                            {workersError ? (
                                <Alert severity="error">{workersError}</Alert>
                            ) : workers == null ? (
                                <TableLoader />
                            ) : workers.length === 0 ? (
                                <Typography variant="body2">No proxy hashrate recorded for this date.</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Worker</TableCell>
                                            <TableCell>User</TableCell>
                                            <TableCell align="right">TH/s</TableCell>
                                            <TableCell align="right">Share</TableCell>
                                            <TableCell>Account</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {workers.map(worker => (
                                            <TableRow key={worker.workerId}>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{worker.workerId}</TableCell>
                                                <TableCell sx={{ fontFamily: 'monospace' }}>{worker.user ?? '-'}</TableCell>
                                                <TableCell align="right">{th(worker.hashRateTh)}</TableCell>
                                                <TableCell align="right">{percent(worker.sharePercent)}</TableCell>
                                                <TableCell>
                                                    {worker.hasAccount
                                                        ? 'Yes'
                                                        : <Chip size="small" color="warning" label="No account: share held" />}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}
